import { createContext, useContext, useEffect, useState, useCallback } from "react";

import { getAuthConfig, AuthConfigResponse } from "@/api/getAuthConfig";

import { keycloakAuthState } from "@/helpers/keycloakAuthState";

type KeycloakAuthState = {
  isKeycloakMode: boolean;
  isLoading: boolean;
  logoutUrl: string;
  issuer: string;
};

const initialState: KeycloakAuthState = {
  isKeycloakMode: false,
  isLoading: true,
  logoutUrl: "",
  issuer: "",
};

const KeycloakAuthContext = createContext<KeycloakAuthState>(initialState);

export const useKeycloakAuth = () => useContext(KeycloakAuthContext);

export const KeycloakAuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [state, setState] = useState<KeycloakAuthState>(initialState);

  const detectAuthMode = useCallback(async () => {
    const config: AuthConfigResponse | null = await getAuthConfig();

    if (config) {
      keycloakAuthState.setKeycloakMode(true);
      setState({
        isKeycloakMode: true,
        isLoading: false,
        logoutUrl: config.logout_url,
        issuer: config.issuer,
      });
    } else {
      keycloakAuthState.setKeycloakMode(false);
      setState({
        isKeycloakMode: false,
        isLoading: false,
        logoutUrl: "",
        issuer: "",
      });
    }
  }, []);

  useEffect(() => {
    // Auth config detection must happen on mount; setState is called async (not synchronously).
    // eslint-disable-next-line react-hooks/set-state-in-effect
    detectAuthMode();
  }, [detectAuthMode]);

  if (state.isLoading) {
    return null;
  }

  return <KeycloakAuthContext.Provider value={state}>{children}</KeycloakAuthContext.Provider>;
};
